import {
  analyticsApi,
  integrationApi,
  type AnalyticsRange,
  type Integration,
  type RevenueActivity,
  type RevenueCurrencySummary,
  type RevenueMode,
  type RevenueProvider,
  type RevenueSubscriptionLifecycle,
} from '@shared/api-client';
import { analyticsRangeLabel } from '@shared/api-client/analytics-range';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownRight,
  ArrowUpRight,
  CircleCheck,
  CircleX,
  CreditCard,
  RotateCcw,
  WalletCards,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  RevenueLineChart,
  type RevenueChartMetric,
} from '@/components/analytics/revenue-line-chart';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { HelpTooltip } from '@/components/ui/help-tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  calculatePercentChange,
  cn,
  formatCurrencyMinor,
  formatRelative,
} from '@/lib/utils';

const METRICS: Array<{
  id: RevenueChartMetric;
  label: string;
  current: keyof RevenueCurrencySummary;
  previous: keyof RevenueCurrencySummary;
  positiveIsGood: boolean;
}> = [
  {
    id: 'netCollectedMinor',
    label: 'Net collected',
    current: 'netCollectedMinor',
    previous: 'previousNetCollectedMinor',
    positiveIsGood: true,
  },
  {
    id: 'grossCollectedMinor',
    label: 'Gross collected',
    current: 'grossCollectedMinor',
    previous: 'previousGrossCollectedMinor',
    positiveIsGood: true,
  },
  {
    id: 'refundsMinor',
    label: 'Refunds',
    current: 'refundsMinor',
    previous: 'previousRefundsMinor',
    positiveIsGood: false,
  },
];

const RevenueChange = ({
  current,
  previous,
  positiveIsGood,
}: {
  current: number;
  previous: number;
  positiveIsGood: boolean;
}) => {
  const change = calculatePercentChange(current, previous);
  if (change === null) {
    return (
      <span
        className={cn(
          'text-xs font-medium',
          positiveIsGood ? 'text-positive' : 'text-critical',
        )}
      >
        New
      </span>
    );
  }
  if (Math.abs(change) < 0.5) {
    return <span className="text-xs text-ink-subtle">0%</span>;
  }

  const rising = change > 0;
  const favorable = rising === positiveIsGood;
  const Icon = rising ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-medium',
        favorable ? 'text-positive' : 'text-critical',
      )}
    >
      <Icon className="size-3.5" />
      {Math.abs(change).toFixed(0)}%
    </span>
  );
};

const integrationLabel = (integration: Integration): string => {
  if (integration.status === 'backfilling') {
    return integration.provider === 'revenuecat'
      ? 'Refreshing provider metrics'
      : 'Importing history';
  }
  if (integration.status === 'error') {
    return 'Sync needs attention';
  }
  if (integration.status === 'connected') {
    return 'Preparing first sync';
  }
  return `${integration.provider === 'revenuecat' ? 'RevenueCat' : 'Stripe'} synced`;
};

const RevenueCatSnapshot = ({
  summary,
  currency,
  range,
  activity,
  subscriptionLifecycle,
}: {
  summary: RevenueCurrencySummary;
  currency: string;
  range: AnalyticsRange;
  activity: RevenueActivity[];
  subscriptionLifecycle: RevenueSubscriptionLifecycle | null;
}) => (
  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
    <Card className="overflow-hidden shadow-none">
      <div className="border-b border-border px-5 py-4">
        <p className="text-xs font-medium text-ink-subtle">
          {range.key === '24h' ? 'Today (UTC)' : analyticsRangeLabel(range)}
        </p>
        <div className="mt-2 flex flex-wrap items-end gap-x-3 gap-y-1">
          <span className="text-3xl font-semibold tracking-tight text-ink tabular-nums">
            {formatCurrencyMinor(summary.grossCollectedMinor, currency, true)}
          </span>
          <RevenueChange
            current={summary.grossCollectedMinor}
            previous={summary.previousGrossCollectedMinor}
            positiveIsGood
          />
        </div>
        <p className="mt-1 text-sm text-ink-muted">
          Gross revenue reported by RevenueCat, compared with the preceding
          period.
        </p>
      </div>

      <dl className="grid divide-y divide-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <div className="px-5 py-4">
          <dt className="text-xs text-ink-subtle">MRR now</dt>
          <dd className="mt-1 text-xl font-semibold text-ink tabular-nums">
            {formatCurrencyMinor(summary.mrrMinor, currency, true)}
          </dd>
        </div>
        <div className="px-5 py-4">
          <dt className="text-xs text-ink-subtle">Active subscriptions</dt>
          <dd className="mt-1 text-xl font-semibold text-ink tabular-nums">
            {summary.activeSubscribers.toLocaleString()}
          </dd>
        </div>
        <div className="px-5 py-4">
          <dt className="text-xs text-ink-subtle">Active trials</dt>
          <dd className="mt-1 text-xl font-semibold text-ink tabular-nums">
            {summary.activeTrials.toLocaleString()}
          </dd>
        </div>
      </dl>
      {subscriptionLifecycle ? (
        <dl className="grid border-t border-border sm:grid-cols-3 sm:divide-x sm:divide-border">
          <div className="border-b border-border px-5 py-4 sm:border-b-0">
            <dt className="flex items-center gap-1 text-xs text-ink-subtle">
              Churned
              <HelpTooltip
                label="About churned subscriptions"
                className="size-5"
              >
                Subscription expirations reported by RevenueCat in the selected
                period. Unlike a cancellation, an expiration means access has
                ended. The preceding period had{' '}
                {subscriptionLifecycle.previousChurned.toLocaleString()}.
              </HelpTooltip>
            </dt>
            <dd
              className={cn(
                'mt-1 text-xl font-semibold tabular-nums',
                subscriptionLifecycle.churned > 0
                  ? 'text-critical'
                  : 'text-ink',
              )}
            >
              {subscriptionLifecycle.churned.toLocaleString()}
            </dd>
          </div>
          <div className="border-b border-border px-5 py-4 sm:border-b-0">
            <dt className="flex items-center gap-1 text-xs text-ink-subtle">
              Cancellations
              <HelpTooltip
                label="About cancellation signals"
                className="size-5"
              >
                Cancellation events reported in the selected period. Most mean
                renewal was turned off, but refunds can also emit this event.
                They are not counted as churn until access expires. The
                preceding period had{' '}
                {subscriptionLifecycle.previousCancellations.toLocaleString()}.
              </HelpTooltip>
            </dt>
            <dd
              className={cn(
                'mt-1 text-xl font-semibold tabular-nums',
                subscriptionLifecycle.cancellations > 0
                  ? 'text-warning'
                  : 'text-ink',
              )}
            >
              {subscriptionLifecycle.cancellations.toLocaleString()}
            </dd>
          </div>
          <div className="px-5 py-4">
            <dt className="flex items-center gap-1 text-xs text-ink-subtle">
              Recovered
              <HelpTooltip
                label="About recovered subscriptions"
                className="size-5"
              >
                Previously cancelled subscriptions that turned renewal back on
                during the selected period. The preceding period had{' '}
                {subscriptionLifecycle.previousRecovered.toLocaleString()}.
              </HelpTooltip>
            </dt>
            <dd
              className={cn(
                'mt-1 text-xl font-semibold tabular-nums',
                subscriptionLifecycle.recovered > 0
                  ? 'text-positive'
                  : 'text-ink',
              )}
            >
              {subscriptionLifecycle.recovered.toLocaleString()}
            </dd>
          </div>
        </dl>
      ) : null}
      <p className="border-t border-border bg-surface-muted/35 px-5 py-3 text-xs leading-relaxed text-ink-muted">
        These totals come from RevenueCat’s Overview and Revenue APIs.
        GainSignal does not recalculate them from subscriber records.
      </p>
    </Card>

    <Card className="overflow-hidden shadow-none">
      <div className="border-b border-border px-5 py-4">
        <h3 className="text-sm font-semibold text-ink">Recent lifecycle</h3>
        <p className="mt-0.5 text-xs text-ink-muted">
          Purchases and subscription changes reported by RevenueCat
        </p>
      </div>
      {activity.length === 0 ? (
        <div className="flex min-h-44 items-center justify-center px-6 text-center text-sm text-ink-muted">
          No purchase or subscription lifecycle event yet.
        </div>
      ) : (
        <ol className="divide-y divide-border px-5">
          {activity.slice(0, 5).map((item) => {
            const label = {
              payment: 'Payment',
              refund: 'Refund',
              failed_payment: 'Billing issue',
              cancellation: 'Subscription cancellation',
              expiration: 'Subscription churned',
              uncancellation: 'Subscription recovered',
            }[item.type];
            const lifecycleStatus =
              item.type === 'cancellation'
                ? 'At risk'
                : item.type === 'expiration'
                  ? 'Access ended'
                  : item.type === 'uncancellation'
                    ? 'Restored'
                    : null;
            return (
              <li
                key={`${item.type}-${item.id}`}
                className="flex items-center justify-between gap-3 py-3"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-ink">
                    {label}
                  </span>
                  <span className="block truncate text-xs text-ink-subtle">
                    {formatRelative(item.occurredAt)}
                  </span>
                </span>
                <span
                  className={cn(
                    'shrink-0 text-sm font-medium tabular-nums',
                    item.type === 'expiration' ||
                      item.type === 'refund' ||
                      item.type === 'failed_payment'
                      ? 'text-critical'
                      : item.type === 'cancellation'
                        ? 'text-warning'
                        : item.type === 'uncancellation'
                          ? 'text-positive'
                          : 'text-ink',
                  )}
                >
                  {lifecycleStatus ?? (
                    <>
                      {item.type === 'payment'
                        ? '+'
                        : item.type === 'refund'
                          ? '−'
                          : ''}
                      {formatCurrencyMinor(
                        Math.abs(item.amountMinor),
                        item.currency,
                        true,
                      )}
                    </>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  </div>
);

export const RevenueOverview = ({
  projectId,
  range,
}: {
  projectId: string;
  range: AnalyticsRange;
}) => {
  const [metric, setMetric] =
    React.useState<RevenueChartMetric>('netCollectedMinor');
  const [preferredMode, setPreferredMode] = React.useState<RevenueMode>('live');
  const [preferredProvider, setPreferredProvider] =
    React.useState<RevenueProvider>('revenuecat');
  const [preferredCurrency, setPreferredCurrency] = React.useState('');
  const integrationsQuery = useQuery({
    queryKey: ['integrations', projectId],
    queryFn: () => integrationApi.listIntegrations(projectId),
    refetchInterval: 10_000,
  });
  const revenueIntegrations = (
    integrationsQuery.data?.integrations ?? []
  ).filter(
    (integration) =>
      (integration.provider === 'stripe' ||
        integration.provider === 'revenuecat') &&
      integration.status !== 'disconnected',
  );
  const availableProviders = [
    ...(revenueIntegrations.some(
      (integration) => integration.provider === 'revenuecat',
    )
      ? (['revenuecat'] as const)
      : []),
    ...(revenueIntegrations.some(
      (integration) => integration.provider === 'stripe',
    )
      ? (['stripe'] as const)
      : []),
  ];
  const provider = availableProviders.includes(preferredProvider)
    ? preferredProvider
    : (availableProviders[0] ?? 'revenuecat');
  const providerIntegrations = revenueIntegrations.filter(
    (integration) => integration.provider === provider,
  );
  const availableModes: RevenueMode[] = [
    ...(providerIntegrations.some((integration) => integration.livemode)
      ? (['live'] as const)
      : []),
    ...(providerIntegrations.some((integration) => !integration.livemode)
      ? (['test'] as const)
      : []),
  ];
  const mode = availableModes.includes(preferredMode)
    ? preferredMode
    : (availableModes[0] ?? 'live');
  const activeIntegration = providerIntegrations.find(
    (integration) => integration.livemode === (mode === 'live'),
  );
  const revenueQuery = useQuery({
    queryKey: [
      'analytics',
      'project',
      projectId,
      'revenue',
      range,
      mode,
      provider,
    ],
    queryFn: () => analyticsApi.getRevenue(projectId, range, mode, provider),
    enabled: Boolean(activeIntegration),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const currencies =
    revenueQuery.data?.summary.map((summary) => summary.currency) ?? [];
  const currency = currencies.includes(preferredCurrency)
    ? preferredCurrency
    : (currencies[0] ?? 'USD');
  const summary = revenueQuery.data?.summary.find(
    (item) => item.currency === currency,
  );
  const points =
    revenueQuery.data?.timeseries.filter(
      (point) => point.currency === currency,
    ) ?? [];
  const customers =
    revenueQuery.data?.customers.filter(
      (customer) => customer.currency === currency,
    ) ?? [];
  const activity =
    revenueQuery.data?.recentActivity.filter(
      (item) => provider === 'revenuecat' || item.currency === currency,
    ) ?? [];

  if (integrationsQuery.isLoading) {
    return <Skeleton className="h-[420px] rounded-card" />;
  }

  if (!activeIntegration) {
    return (
      <section aria-labelledby="revenue-heading">
        <div className="mb-3">
          <h2 id="revenue-heading" className="text-base font-semibold text-ink">
            Revenue
          </h2>
          <p className="mt-0.5 text-sm text-ink-muted">
            Connect billing data to put customer behavior in financial context.
          </p>
        </div>
        <Card className="shadow-none">
          <EmptyState
            icon={WalletCards}
            title="Connect a revenue source"
            description="GainSignal will import payment history, keep it current with webhooks, and show revenue beside product behavior."
            action={
              <Button asChild variant="secondary">
                <Link
                  to={`/projects/${encodeURIComponent(projectId)}/data-sources`}
                >
                  Connect a revenue source
                </Link>
              </Button>
            }
            className="py-12"
          />
        </Card>
      </section>
    );
  }

  return (
    <section aria-labelledby="revenue-heading" className="space-y-4">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2
              id="revenue-heading"
              className="text-base font-semibold text-ink"
            >
              Revenue
            </h2>
            <Badge tone={mode === 'live' ? 'success' : 'warning'}>
              {mode === 'live' ? 'Production' : 'Test mode'}
            </Badge>
          </div>
          <p className="mt-0.5 text-sm text-ink-muted">
            {activeIntegration.displayName
              ? `${activeIntegration.displayName} · `
              : ''}
            {integrationLabel(activeIntegration)}
          </p>
        </div>

        <div className="flex gap-2">
          {availableProviders.length > 1 ? (
            <Select
              value={provider}
              onValueChange={(value) =>
                setPreferredProvider(value as RevenueProvider)
              }
            >
              <SelectTrigger className="w-36" aria-label="Revenue provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="revenuecat">RevenueCat</SelectItem>
                <SelectItem value="stripe">Stripe</SelectItem>
              </SelectContent>
            </Select>
          ) : null}
          {availableModes.length > 1 ? (
            <Select
              value={mode}
              onValueChange={(value) => setPreferredMode(value as RevenueMode)}
            >
              <SelectTrigger className="w-36" aria-label="Revenue data mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="live">Production</SelectItem>
                <SelectItem value="test">Test mode</SelectItem>
              </SelectContent>
            </Select>
          ) : null}
          {currencies.length > 1 ? (
            <Select value={currency} onValueChange={setPreferredCurrency}>
              <SelectTrigger className="w-28" aria-label="Revenue currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
      </div>

      {revenueQuery.isError ? (
        <Alert tone="error">
          Revenue analytics could not be loaded. Collection continues; try again
          shortly.
        </Alert>
      ) : null}

      {revenueQuery.isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-[430px] rounded-card" />
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-72 rounded-card" />
            <Skeleton className="h-72 rounded-card" />
          </div>
        </div>
      ) : revenueQuery.data?.periodStatus === 'unsupported' ? (
        <Card className="p-6 text-sm text-ink-muted shadow-none">
          RevenueCat totals are available for Today, Last 7, 30 and 90 days.
          This range still shows traffic and available growth milestones;
          current MRR is independent of the selected dates.
        </Card>
      ) : !summary ? (
        <Card className="shadow-none">
          <EmptyState
            icon={CreditCard}
            title={
              activeIntegration.status === 'backfilling'
                ? provider === 'revenuecat'
                  ? 'Refreshing RevenueCat metrics'
                  : 'Stripe history is importing'
                : `No ${mode === 'live' ? 'production' : 'test'} revenue yet`
            }
            description={
              activeIntegration.status === 'backfilling'
                ? provider === 'revenuecat'
                  ? 'The first provider snapshot usually appears within a few seconds.'
                  : 'Payments will appear here as soon as the first backfill pages reach analytics.'
                : 'The connection is healthy. Payments, refunds, customers, and subscriptions will appear automatically.'
            }
            className="py-14"
          />
        </Card>
      ) : provider === 'revenuecat' ? (
        <RevenueCatSnapshot
          summary={summary}
          currency={currency}
          range={range}
          activity={activity}
          subscriptionLifecycle={
            revenueQuery.data?.subscriptionLifecycle ?? null
          }
        />
      ) : (
        <>
          <Card className="overflow-hidden shadow-none">
            <div className="grid lg:grid-cols-[minmax(0,1fr)_18rem]">
              <div className="min-w-0">
                <div className="flex overflow-x-auto border-b border-border">
                  {METRICS.map((definition) => {
                    const current = Number(summary[definition.current]);
                    const previous = Number(summary[definition.previous]);
                    return (
                      <button
                        key={definition.id}
                        type="button"
                        onClick={() => setMetric(definition.id)}
                        className={cn(
                          'min-w-44 flex-1 border-r border-border px-4 py-3 text-left transition-colors last:border-r-0',
                          metric === definition.id
                            ? 'bg-surface-muted'
                            : 'hover:bg-surface-muted/50',
                        )}
                        aria-pressed={metric === definition.id}
                      >
                        <span className="block text-xs font-medium text-ink-subtle">
                          {definition.label}
                        </span>
                        <span className="mt-1 flex items-baseline gap-2">
                          <span className="text-lg font-semibold text-ink tabular-nums">
                            {formatCurrencyMinor(current, currency, true)}
                          </span>
                          {range.comparisonEnabled !== false && (
                            <RevenueChange
                              current={current}
                              previous={previous}
                              positiveIsGood={definition.positiveIsGood}
                            />
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="p-4 sm:p-5">
                  <div className="mb-1 flex items-center justify-between gap-3 text-xs text-ink-subtle">
                    <span>{analyticsRangeLabel(range)}</span>
                    {range.comparisonEnabled !== false && (
                      <span>Compared with preceding period</span>
                    )}
                  </div>
                  <RevenueLineChart
                    points={points}
                    metric={metric}
                    currency={currency}
                    range={range.key}
                  />
                </div>
              </div>

              <div className="border-t border-border bg-surface-muted/35 p-5 lg:border-l lg:border-t-0">
                <h3 className="text-sm font-semibold text-ink">
                  Business state
                </h3>
                <p className="mt-1 text-xs text-ink-muted">
                  Current recurring base and imported lifetime context.
                </p>
                <dl className="mt-5 divide-y divide-border">
                  <div className="pb-4">
                    <dt className="text-xs text-ink-subtle">Current MRR</dt>
                    <dd className="mt-1 text-2xl font-semibold tracking-tight text-ink tabular-nums">
                      {formatCurrencyMinor(summary.mrrMinor, currency, true)}
                    </dd>
                    <p className="mt-1 text-xs text-ink-muted">
                      {summary.activeSubscribers}{' '}
                      {summary.activeSubscribers === 1
                        ? 'active subscriber'
                        : 'active subscribers'}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-4 py-3 text-sm">
                    <dt className="text-ink-muted">Lifetime net collected</dt>
                    <dd className="font-medium text-ink tabular-nums">
                      {formatCurrencyMinor(
                        summary.lifetimeNetCollectedMinor,
                        currency,
                        true,
                      )}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4 py-3 text-sm">
                    <dt className="text-ink-muted">Paying customers</dt>
                    <dd className="font-medium text-ink tabular-nums">
                      {summary.payingCustomers}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4 py-3 text-sm">
                    <dt className="text-ink-muted">Successful payments</dt>
                    <dd className="font-medium text-ink tabular-nums">
                      {summary.payments}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4 pt-3 text-sm">
                    <dt className="text-ink-muted">Failed attempts</dt>
                    <dd
                      className={cn(
                        'font-medium tabular-nums',
                        summary.failedPayments > 0
                          ? 'text-critical'
                          : 'text-ink',
                      )}
                    >
                      {summary.failedPayments}
                    </dd>
                  </div>
                </dl>
                <p className="mt-5 border-t border-border pt-3 text-xs leading-relaxed text-ink-subtle">
                  Net collected is captured payments minus successful refunds.
                  {provider === 'stripe'
                    ? 'Stripe fees and disputes are not deducted.'
                    : 'RevenueCat values use the store-reported purchased currency.'}
                </p>
              </div>
            </div>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="overflow-hidden shadow-none">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-sm font-semibold text-ink">
                  Top customers
                </h3>
                <p className="mt-0.5 text-xs text-ink-muted">
                  Net collected in {analyticsRangeLabel(range).toLowerCase()}
                </p>
              </div>
              {customers.length === 0 ? (
                <div className="flex min-h-56 items-center justify-center px-6 text-center text-sm text-ink-muted">
                  No paying customers in this period.
                </div>
              ) : (
                <ol className="p-4">
                  {customers.map((customer) => {
                    const maximum = Math.max(
                      ...customers.map((item) => item.netCollectedMinor),
                      1,
                    );
                    const identity =
                      customer.customerName ||
                      customer.customerEmail ||
                      customer.customerId ||
                      'Guest checkout';
                    return (
                      <li
                        key={`${customer.customerId}-${customer.currency}`}
                        className="relative flex min-h-12 items-center gap-3 px-2"
                      >
                        <span
                          className="absolute inset-y-1 left-0 rounded bg-surface-muted"
                          style={{
                            width: `${Math.max(
                              (customer.netCollectedMinor / maximum) * 100,
                              2,
                            )}%`,
                          }}
                          aria-hidden
                        />
                        <span className="relative min-w-0 flex-1">
                          <span
                            className="block truncate text-sm text-ink"
                            title={identity}
                          >
                            {identity}
                          </span>
                          <span className="block text-xs text-ink-subtle">
                            {customer.payments}{' '}
                            {customer.payments === 1 ? 'payment' : 'payments'}
                            {customer.refundsMinor > 0
                              ? ` · ${formatCurrencyMinor(customer.refundsMinor, currency, true)} refunded`
                              : ''}
                          </span>
                        </span>
                        <span className="relative shrink-0 text-sm font-medium tabular-nums text-ink">
                          {formatCurrencyMinor(
                            customer.netCollectedMinor,
                            currency,
                            true,
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>

            <Card className="overflow-hidden shadow-none">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-sm font-semibold text-ink">
                  Recent Stripe activity
                </h3>
                <p className="mt-0.5 text-xs text-ink-muted">
                  Latest imported payments and refunds, including history
                </p>
              </div>
              {activity.length === 0 ? (
                <div className="flex min-h-56 items-center justify-center px-6 text-center text-sm text-ink-muted">
                  No payment activity imported yet.
                </div>
              ) : (
                <ol className="divide-y divide-border px-5">
                  {activity.map((item) => {
                    const Icon =
                      item.type === 'refund'
                        ? RotateCcw
                        : item.type === 'failed_payment'
                          ? CircleX
                          : CircleCheck;
                    const label =
                      item.type === 'refund'
                        ? 'Refund'
                        : item.type === 'failed_payment'
                          ? 'Failed payment'
                          : 'Payment';
                    const identity =
                      item.customerName ||
                      item.customerEmail ||
                      item.customerId ||
                      'Guest checkout';
                    return (
                      <li
                        key={`${item.type}-${item.id}`}
                        className="flex items-center gap-3 py-3"
                      >
                        <span
                          className={cn(
                            'flex size-8 shrink-0 items-center justify-center rounded-lg',
                            item.type === 'payment'
                              ? 'bg-positive-subtle text-positive'
                              : item.type === 'refund'
                                ? 'bg-surface-muted text-ink-muted'
                                : 'bg-critical-subtle text-critical',
                          )}
                        >
                          <Icon className="size-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="text-sm font-medium text-ink">
                              {label}
                            </span>
                            <span className="truncate text-xs text-ink-subtle">
                              {identity}
                            </span>
                          </span>
                          <span className="mt-0.5 block text-xs text-ink-muted">
                            {item.cardBrand
                              ? `${item.cardBrand.toUpperCase()} · `
                              : ''}
                            {formatRelative(item.occurredAt)}
                          </span>
                        </span>
                        <span
                          className={cn(
                            'shrink-0 text-sm font-medium tabular-nums',
                            item.type === 'payment'
                              ? 'text-ink'
                              : 'text-critical',
                          )}
                        >
                          {item.type === 'payment'
                            ? '+'
                            : item.type === 'refund'
                              ? '−'
                              : ''}
                          {formatCurrencyMinor(
                            Math.abs(item.amountMinor),
                            currency,
                            true,
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>
          </div>
        </>
      )}
    </section>
  );
};
